<template>
    <div class="editable-editor-wrapper" :class="{ focused, [` ${classNames}`]: true }">
        <editor-content :editor="editor" class="editable-editor" :class="{ focused }" />
        <div
            class="text-editor-utils"
            v-tippy="{ followCursor: true, placement: 'bottom-end' }"
            :content="getRemainingCharacterCount() <= 5 ? this.$t('message.textLimitAlertMessage') : ''"
        >
            <div
                class="text-editor-insert-attribute"
                v-if="showAttributes && !previewMode"
                @click="() => appendAttribute()"
            >
                <i class="material-icons"> data_object </i>
            </div>
            <a-popover
                v-model:visible="showEmojiPicker"
                @keydown.esc="showEmojiPicker = false"
                trigger="click"
                placement="leftBottom"
                :overlayClassName="'no-padding'"
                v-if="showEmoji && !previewMode"
            >
                <template #content>
                    <div class="emoji-container">
                        <ModalEmojiPicker :onPickedEmoji="_handleEmojiPicked" />
                    </div>
                </template>
                <div class="emoji-picker">
                    <div class="emoji-picker-button" aria-expanded="false">
                        <i class="material-icons"> sentiment_very_satisfied </i>
                    </div>
                </div>
            </a-popover>
            <div
                class="text-limit"
                v-if="maxCharacterCount && !previewMode"
                :class="{ error: getRemainingCharacterCount() <= 10 }"
            >
                {{ getRemainingCharacterCount() }}
            </div>
        </div>
    </div>
</template>
<script>
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import HardBreak from "@tiptap/extension-hard-break";
import { Editor, EditorContent } from "@tiptap/vue-2";
import SezarAttributeTipTapExtension from "./tiptap-extensions/SezarAttributeTipTapExtension";
import AutomationAttributeTipTapExtension from "../../../../Automation/pages/dashboard/common/tiptap-extensions/AutomationAttributeTipTapExtension";
import SezarParameterValidatedAttributeTipTapExtension from "./tiptap-extensions/SezarParameterValidatedAttributeTipTapExtension";
import { generateRandomId } from "../../../../../../utils/session";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import EmojiPicker from "../../../../../../components/popup-chat/EmojiPicker";
import ModalEmojiPicker from "./ModalEmojiPicker";

const log = log2("TiptapEditor");

const lineSeparator = '<br class="break">';
export default {
    name: "TiptapEditor",
    props: {
        value: { type: String | Number, default: "", required: false },
        placeholder: { type: String, default: "Type here", required: false },
        classNames: { type: String, default: "", required: false },
        maxCharacterCount: { type: Number, default: null, required: false },
        showEmoji: { type: Boolean, default: true, required: false },
        showAttributes: { type: Boolean, default: true, required: false },
        rows: { type: Number, default: 1, required: false },
        previewMode: { type: Boolean, default: false, required: false },
        enterEnabled: { type: Boolean, default: true },
        parameterValidationMode: { type: Boolean, default: false },
        automationMode: { type: Boolean, default: false },
        allowedAttributes: {
            type: Array,
            default: () => [],
        },
        whatsappMode: {
            type: Boolean,
            default: false,
        },
        omitValidation: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        ModalEmojiPicker,
        EditorContent,
        EmojiPicker,
    },
    data() {
        return {
            showPlaceholder: _.isEmpty(!this.value),
            editor: null,
            attributedIds: [],
            focused: false,
            showEmojiPicker: false,
            latestAddedAttributeId: null,
        };
    },
    methods: {
        appendAttribute() {
            let attribute = "name";
            if (this.parameterValidationMode) attribute = "any";

            if (this.previewMode) return;
            log("appendAttribute", attribute);
            const encodedAttribute = this.parameterValidationMode ? `{{any___${attribute}}}` : `{{${attribute}}}`;
            this.editor.chain().insertContent(` ${encodedAttribute}`).run();
            this.editor.chain().focus().run();
            setTimeout(() => {
                this.editor.chain().focus().run();
            }, 150);
            setTimeout(() => {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_SHOW_ATTRIBUTE_MODAL, {
                    attributeId: this.latestAddedAttributeId,
                });
            }, 250);
        },

        markAttributes(str, openModalLastAttribute = false) {
            const r = new RegExp(/{{(.*?)}}/g);
            let result = str.replace(r, (match) => {
                const attrId = generateRandomId(10);
                this.attributedIds.push(attrId);
                this.latestAddedAttributeId = attrId;
                if (this.automationMode) {
                    const attributeName = match.replace(/[{}]/g, "");
                    return `<automation-attribute-editor name="${attributeName}" data-id="${attrId}" ></automation-attribute-editor>`;
                } else if (this.parameterValidationMode) {
                    const encodedValue = match.replace(/[{}]/g, "");
                    const [validationType, attributeName] = encodedValue.split("___");
                    return `<sezar-parameter attributeName="${attributeName}" validationType="${validationType}" data-id="${attrId}" omitValidation="${this.omitValidation.join(
                        ","
                    )}"></sezar-parameter>`;
                } else {
                    const attributeName = match.replace(/[{}]/g, "");
                    const whatsappMode = this.whatsappMode;
                    const allowedAttributes = this.allowedAttributes;
                    return `<sezar name="${attributeName}" data-id="${attrId}"  whatsappMode="${whatsappMode}" allowedAttributes="${allowedAttributes}" ></sezar>`;
                }
            });
            if (openModalLastAttribute && this.latestAddedAttributeId) {
                setTimeout(() => {
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_SHOW_ATTRIBUTE_MODAL, {
                        attributeId: this.latestAddedAttributeId,
                    });
                }, 250);
            }
            return result;
        },
        subscribeAttrChanges() {
            this.subscriber = SYSTEM_PUBSUB.stream.subscribe(({ type, payload }) => {
                if (type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_ATTRIBUTE_CHANGED) {
                    const { attributeId } = payload;
                    if (this.attributedIds.includes(attributeId) && this.editor) {
                        log("CB_ATTRIBUTE_CHANGED event", { payload, allIds: this.attributedIds });
                        log("change attr with id", {
                            attributeId,
                            html: this.editor.getHTML(),
                            parameterValidationMode: this.parameterValidationMode,
                            automationMode: this.automationMode,
                        });
                        const html = this.editor.getHTML();
                        const el = document.createElement("div");
                        el.innerHTML = html;
                        const foundSezar = Array.from(
                            el.querySelectorAll(
                                this.parameterValidationMode
                                    ? "sezar-parameter"
                                    : this.automationMode
                                    ? "automation-attribute-editor"
                                    : "sezar"
                            )
                        ).find((x) => x.getAttribute("data-id") === attributeId);
                        console.log("CB_ATTRIBUTE_CHANGED found elem", foundSezar);
                        if (foundSezar) {
                            if (this.parameterValidationMode) {
                                const { attributeName, validationType } = payload;
                                foundSezar.setAttribute("attributeName", attributeName);
                                foundSezar.setAttribute("validationType", validationType);
                            } else if (this.automationMode) {
                                const { name } = payload;
                                foundSezar.setAttribute("name", name);
                            } else {
                                const { name } = payload;
                                foundSezar.setAttribute("name", name);
                            }

                            this.editor.chain().setContent(el.innerHTML).run();
                            const value = this.unbindAttributes(this.editor.getHTML());
                            if (!_.isEqual(value, this.value)) {
                                this.emitValueDelayed(value);
                            }
                        }
                    }
                }
            });
        },
        hasAttributes(str) {
            const r = new RegExp(/{{(.*?)}}/g);
            const result = r.test(str);
            // log("hasAttributes", { str, result });
            return result;
        },
        hasHalfClosedAttributes(str = "") {
            const result = str.indexOf("{{") !== -1;
            // log("hasHalfClosedAttributes", { str, result });
            return result;
        },
        unbindAttributes(html) {
            let result = "";
            if (this.parameterValidationMode) {
                const r = new RegExp("(<sezar-parameter(.*?)>(.*?)<\\/sezar-parameter>)", "gmi");
                result = html.replace(r, (match) => {
                    const el = document.createElement("div");
                    el.innerHTML = match;
                    const attributeName = el.querySelector("sezar-parameter").getAttribute("attributeName");
                    const validationType = el.querySelector("sezar-parameter").getAttribute("validationType");
                    return `{{${validationType}___${attributeName}}}`;
                });
            } else if (this.automationMode) {
                const r = new RegExp(
                    "(<automation-attribute-editor(.*?)>(.*?)<\\/automation-attribute-editor>)",
                    "gmi"
                );
                result = html.replace(r, (match) => {
                    const el = document.createElement("div");
                    el.innerHTML = match;
                    const name = el.querySelector("automation-attribute-editor").getAttribute("name");
                    return `{{${name}}}`;
                });
            } else {
                const r = new RegExp("(<sezar(.*?)>(.*?)<\\/sezar>)", "gmi");
                result = html.replace(r, (match) => {
                    const el = document.createElement("div");
                    el.innerHTML = match;
                    const attributeName = el.querySelector("sezar").getAttribute("name");
                    return `{{${attributeName}}}`;
                });
            }

            const regexp = new RegExp(`(${lineSeparator}){3}`, "g");
            const el = document.createElement("div");
            el.innerHTML = result;
            const pChild = el.children[0];
            result =
                pChild.childNodes.length <= 1 ? el.innerText : result.replace(regexp, `${lineSeparator.repeat(2)}`);
            return result;
        },

        emitValue(_value) {
            log("emitValue", { value: this.value });
            if (!this.editor) return;
            const value = _value || this.unbindAttributes(this.editor.getHTML());

            if (_.isEqual(value, this.value)) {
                log("value not changed");
                return;
            }
            log("value sending", { value });
            this.$emit("input", value);
            this.$emit("change", value);
        },
        emitValueDelayed(value, timeout = 500) {
            log("emitValueDelayed", { value, timeout });
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
            if (!timeout) {
                this.emitValue(value);
                this.$emit("change", value);
            } else {
                log("setTimeout called", { value, timeout });
                this.timeoutId = setTimeout(() => {
                    log("setTimeout body running", { value, timeout });
                    this.$emit("input", value);
                    this.$emit("change", value);
                }, 500);
            }
        },
        getValueLength() {
            return !this.editor ? 0 : (this.unbindAttributes(this.editor.getHTML()) || "").length;
        },
        getRemainingCharacterCount() {
            return this.maxCharacterCount - this.getValueLength();
        },
        _handleEmojiPicked(emoji) {
            if (this.previewMode) return;
            this.editor.chain().insertContent(emoji.data).run();
            this.showEmojiPicker = false;
        },
        getEditorValue() {
            return this.enterEnabled
                ? this.editor.getHTML()
                : this.editor.getHTML().replaceAll("<p></p>", lineSeparator);
        },
    },
    mounted() {
        log("TiptapEditor mounted ", { previewMode: this.previewMode });
        this.subscribeAttrChanges();
        const self = this;
        this.editor = new Editor({
            extensions: [
                StarterKit.configure({
                    bold: false,
                    code: false,
                    italic: false,
                    heading: false,
                    codeBlock: false,
                    blockquote: false,
                    strike: false,
                    orderedList: false,
                }),
                // StarterKit,
                SezarAttributeTipTapExtension,
                SezarParameterValidatedAttributeTipTapExtension,
                AutomationAttributeTipTapExtension,
                Placeholder.configure({
                    placeholder: this.placeholder || "Type here",
                    emptyEditorClass: "empty-editor",
                    showOnlyWhenEditable: true,
                    includeChildren: false,
                }),
                HardBreak.configure({
                    HTMLAttributes: {
                        class: "break",
                    },
                }),
            ],
            enablePasteRules: false,
            enableInputRules: false,
            injectCSS: false,
            editable: !this.previewMode,
            content: this.markAttributes(this.value || ""),
            onUpdate: ({ editor, transaction }) => {
                if (this.previewMode) return;
                const text = this.getEditorValue();

                const hasAttributes = this.hasAttributes(text);
                if (hasAttributes) {
                    let newText = this.markAttributes(text);
                    if (this.hasHalfClosedAttributes(newText)) {
                        newText = this.markAttributes(
                            newText.replace(/{{/g, this.parameterValidationMode ? " {{any___any}} " : " {{name}} ")
                        );
                    }
                    editor.chain().setContent(newText).run();
                } else {
                    if (this.hasHalfClosedAttributes(text)) {
                        const newText = this.markAttributes(
                            text.replace(/{{/g, this.parameterValidationMode ? " {{any___any}} " : " {{name}} "),
                            true
                        );
                        editor.chain().setContent(newText).run();
                    }
                }
                const value = this.unbindAttributes(this.getEditorValue());
                if (!_.isEqual(value, this.value)) {
                    this.emitValueDelayed(value, 0);
                }
            },
            onFocus: ({ editor, event }) => {
                log("onFocus", { editor, event });
                self.focused = true;
            },
            onBlur: ({ editor, event }) => {
                // The editor isn’t focused anymore.
                log("onBlur", { editor, event });
                setTimeout(() => {
                    self.focused = false;
                }, 100);
            },
        });

        log("TiptapEditor editor", { editor: this.editor });
    },
    beforeDestroy() {
        log("TiptapEditor beforeDestroy ", {});
        this.emitValue();
        this.editor.destroy();
        this.subscriber.unsubscribe();
    },

    watch: {
        value: {
            handler(newValue) {
                log("TiptapEditor value changed", { newValue });
            },
            immediate: true,
        },
    },
};
</script>

<style lang="scss">
//br.ProseMirror-trailingBreak {
//    display: none;
//}
p {
    &.is-empty,
    &.empty-editor {
        min-height: 10px !important;
    }
}

.ProseMirror {
    > * + * {
        margin-top: 0.75em;
    }
}

.ant-popover {
    &.ant-popover-placement-leftBottom {
        top: 216px !important;
    }
}

/* Placeholder (at the top) */
.ProseMirror p.is-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
}

.editable-editor-wrapper {
    user-select: auto;
    width: 100%;
    position: relative;

    .text-editor-utils {
        align-items: center;
        display: flex;
        bottom: -10px;
        position: absolute;
        right: 5px;
        z-index: 16;

        .text-editor-insert-attribute {
            align-items: center;
            background: #fff;
            border-radius: 100%;
            box-shadow: 0 1px 3px 0 rgb(0 0 0 / 19%);
            cursor: pointer;
            display: flex;
            height: 23px;
            justify-content: center;
            margin-right: 5px;
            transition: background-color 0.3s;
            width: 23px;

            &:hover {
                background: #006cff !important;
                color: #fff !important;
            }

            .material-icons {
                font-size: 16px;
            }
        }

        .emoji-picker-button {
            align-items: center;
            background: #fff;
            border-radius: 100%;
            box-shadow: 0 1px 3px 0 rgb(0 0 0 / 19%);
            cursor: pointer;
            display: flex;
            height: 23px;
            justify-content: center;
            margin-right: 5px;
            transition: background-color 0.3s;
            width: 23px;

            .material-icons {
                font-size: 16px;
            }

            &:hover {
                background: #006cff !important;
                color: #fff !important;
            }
        }

        .text-limit {
            border-radius: 10px;
            flex-shrink: 0;
            font-size: 12px;
            font-weight: 700;
            line-height: 16px;
            padding: 0 10px;
            pointer-events: none;
            transition: background-color 0.3s, color 0.3s;
            background: #647283;
            color: #fff;

            &.error {
                background: #fb6e6e;
                color: #fff;
            }
        }
    }

    .editable-editor {
        width: 100%;
        height: 100%;
        border-radius: 12px;
        padding: 6px 12px;
        background-color: #ffffff;
        position: relative;
        white-space: normal !important;
        overflow: hidden;
        text-overflow: ellipsis;

        p {
            margin-bottom: 0 !important;
        }

        &.focused {
            overflow: auto;
        }

        &:focus {
        }

        &:hover {
            resize: none;
        }

        &.placeholder {
            pointer-events: none;
            display: inline-block;
            color: #516173;
            height: 35px;
            width: 100%;
            font-size: 16px;
            line-height: 25px;
            padding: 5px 20px 0;
            word-wrap: break-word;
            font-feature-settings: "liga" 0;
            font-variant-ligatures: none;
            position: relative;
            white-space: break-spaces;

            &:after {
                content: attr(data-placeholder);
            }
        }
    }
}
</style>
